import {
  BuildingImportDto,
  EntrancePlantDetailDTO,
  EntrancePlantInfoCreateDTO,
  PasspartoutModes,
} from '@/../client-generator/generated-clients/plants';
import { mapState } from 'vuex';
import { Options, Vue } from 'vue-class-component';
import { SET_APP_LOADING } from '@/core/store/action-types';
import plantsClient from '@/core/services/plants';
import ToggleSwitchButton from '@/views/components/ToggleSwitchButton/ToggleSwitchButton.vue';
import KeypadCodeInput from '@/views/components/KeypadCodeInput/KeypadCodeInput.vue';
import DialogConfirmDelete from '@/views/components/DialogConfirmDelete/DialogConfirmDelete.vue';
import { getBuildingsKeypadCodes, getGuestsKeypadCodes } from '@/core/utils/EntrancePlant/entrance-plant.utils';

interface KeypadCode {
  passpartoutEnabled?: boolean;
  sameCode?: boolean;
  passpartoutCode?: string;
  requiredLength?: number;
}

@Options({
  name: 'AccessCodeTab',
  props: {
    infoPlant: {
      type: EntrancePlantInfoCreateDTO,
      required: true,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant', 'adminView']),
  },
  components: {
    DialogConfirmDelete,
    ToggleSwitchButton,
    KeypadCodeInput,
  },
  watch: {
    'keypadCode.sameCode': function () {
      this.setPasspartoutMode();
    },
    'keypadCode.passpartoutEnabled': function () {
      this.setPasspartoutMode();
    },
  },
  emits: ['update-list'],
})
export default class AccessCodeTab extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  adminView!: boolean;
  infoPlant!: EntrancePlantInfoCreateDTO;
  keypadCode: KeypadCode = {
    passpartoutEnabled: false,
    sameCode: true,
    passpartoutCode: '',
    requiredLength: 5,
  };
  savedBuildingsCode: {
    buildingId?: string;
    buildingName?: string;
    keypadCodePasspartoutCode?: string;
    codeChanged: boolean;
  }[] = [];
  lengthOptions = [4, 5, 6, 7, 8, 9];
  passpartoutMode = PasspartoutModes.Disabled;
  errorKeyCode = false;
  codeChanged = false;
  lengthChoice?: number;
  showLengthWarning = false;
  drawAttention = false;
  forceUpdateKey = 0;

  get lengthValue() {
    return this.keypadCode.requiredLength;
  }

  set lengthValue(value: number | undefined) {
    if (value !== this.keypadCode.requiredLength) {
      this.lengthChoice = value;
      this.showLengthWarning = true;
    }
  }

  get saveDisabled() {
    return this.noChanges || this.hasError;
  }

  get noChanges() {
    const length =
      this.keypadCode.requiredLength === this.infoPlant.keypadCodeRequiredLength;
    const mode =
      this.passpartoutMode === this.infoPlant.keypadCodePasspartoutMode;

    const code =
      this.passpartoutMode === PasspartoutModes.SameEntirePlant
        ? !this.codeChanged
        : this.savedBuildingsCode.every((building) => !building.codeChanged);

    return length && mode && code;
  }

  get hasError() {
    let keypadCodeEmpty = false;
    if (this.passpartoutMode === PasspartoutModes.SameEntirePlant) {
      keypadCodeEmpty =
        this.keypadCode.passpartoutCode === '' ||
        this.keypadCode.passpartoutCode === undefined;
    }
    if (this.passpartoutMode === PasspartoutModes.DifferentEachBuilding) {
      keypadCodeEmpty = this.savedBuildingsCode.some(
        (building) =>
          building.keypadCodePasspartoutCode === '' ||
          building.keypadCodePasspartoutCode === undefined
      );
    }
    return keypadCodeEmpty || this.errorKeyCode;
  }

  getAlreadyUsedCodes(buildingId?: string) {
    const guestsCodes = getGuestsKeypadCodes(this.currentEntrancePlant);
    const buildingsCodes = getBuildingsKeypadCodes(this.currentEntrancePlant);
    const plantCode = this.currentEntrancePlant.keypadCodePasspartoutCode ?? '';
    const tempOtherBuildingsCodes = this.savedBuildingsCode
      .filter((building) => building.buildingId !== buildingId)
      .map((building) => building.keypadCodePasspartoutCode ?? '');

    return [...guestsCodes, ...buildingsCodes, ...tempOtherBuildingsCodes, plantCode].filter(code => code !== '');
  }

  created(): void {
    this.initializeData();
    this.initializeSavedBuildingsCode();
  }

  initializeData() {
    switch (this.infoPlant.keypadCodePasspartoutMode) {
      case PasspartoutModes.Disabled:
        this.keypadCode.passpartoutEnabled = false;
        break;

      case PasspartoutModes.SameEntirePlant:
        this.keypadCode.passpartoutEnabled = true;
        this.keypadCode.sameCode = true;
        break;

      case PasspartoutModes.DifferentEachBuilding:
        this.keypadCode.passpartoutEnabled = true;
        this.keypadCode.sameCode = false;
        break;
    }
    this.keypadCode.passpartoutCode = this.infoPlant.keypadCodePasspartoutCode;
    this.keypadCode.requiredLength = this.infoPlant.keypadCodeRequiredLength;
  }

  initializeSavedBuildingsCode() {
    if (!this.currentEntrancePlant.buildings) this.savedBuildingsCode = [];
    else
      this.savedBuildingsCode = this.currentEntrancePlant.buildings.map(
        (building) => ({
          buildingId: building.buildingId,
          buildingName: building.name,
          keypadCodePasspartoutCode: building.keypadCodePasspartoutCode,
          codeChanged: false,
        })
      );
  }

  handleKeyCodeEdited(value: boolean, keyCode: string) {
    this.codeChanged = value;

    if (!this.errorKeyCode) {
      this.keypadCode.passpartoutCode = keyCode;
    }
  }

  handleBuildingKeyCodeEdited(value: boolean, keyCode: string, index: number) {
    this.savedBuildingsCode[index].codeChanged = value;
  }

  handleErrorKeyCode(value: boolean) {
    this.errorKeyCode = value;
  }

  setPasspartoutMode() {
    if (this.keypadCode.passpartoutEnabled) {
      if (this.keypadCode.sameCode) {
        this.passpartoutMode = PasspartoutModes.SameEntirePlant;
        this.savedBuildingsCode.forEach(
          (building) => (building.keypadCodePasspartoutCode = '')
        );
      } else {
        this.passpartoutMode = PasspartoutModes.DifferentEachBuilding;
        this.keypadCode.passpartoutCode = '';
      }
    } else {
      this.passpartoutMode = PasspartoutModes.Disabled;
      this.keypadCode.passpartoutCode = '';
      this.savedBuildingsCode.forEach(
        (building) => (building.keypadCodePasspartoutCode = '')
      );
    }
  }

  async confirmChangeLength() {
    this.keypadCode.requiredLength = this.lengthChoice;
    if (this.keypadCode.passpartoutEnabled) {
      this.keypadCode.passpartoutCode = '';
      this.codeChanged = true;
      this.savedBuildingsCode.forEach((building) => {
        building.keypadCodePasspartoutCode = '';
        building.codeChanged = true;
      });
      this.drawAttention = true;
    } else {
      await this.handleSaveCode();
    }
    this.showLengthWarning = false;
    this.forceRerender();
  }

  async handleSaveCode() {
    this.$store.dispatch(SET_APP_LOADING, true);

    await this.saveCode();
    this.$emit('update-list');
    this.forceRerender();
    this.$store.dispatch(SET_APP_LOADING, false);
  }

  async saveCode() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    try {
      this.infoPlant.keypadCodePasspartoutMode = this.passpartoutMode;
      this.infoPlant.keypadCodePasspartoutCode =
        this.passpartoutMode === PasspartoutModes.SameEntirePlant
          ? this.keypadCode.passpartoutCode
          : '';
      this.infoPlant.keypadCodeRequiredLength = this.keypadCode.requiredLength;
      this.codeChanged = false;

      this.infoPlant.buildings = [];
      for (const editedBuilding of this.savedBuildingsCode) {
        if (!editedBuilding.buildingId) continue;
        this.infoPlant.buildings?.push(
          new BuildingImportDto({
            buildingId: editedBuilding.buildingId,
            keypadCode: this.passpartoutMode === PasspartoutModes.DifferentEachBuilding
              ? editedBuilding.keypadCodePasspartoutCode
              : '',
          })
        );
        editedBuilding.codeChanged = false;
      }

      await plantsClient.editinfo(
        this.currentEntrancePlant.desktopId,
        this.infoPlant
      );
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    }
  }

  forceRerender = () => {
    this.forceUpdateKey += 1;
  };
}
